import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@trellis/roots/sheet'
import { Skeleton } from '@trellis/roots/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@trellis/roots/tabs'
import { Typography } from '@trellis/roots/typography'
import { ReminderEmailStageSchema } from '@trellis/shared'
import type { ReminderEmailStage } from '@trellis/shared'

import { useReminderEmailPreviewQuery } from '@/hooks/useReminders'

const STAGE_LABELS: Record<ReminderEmailStage, string> = {
  BEFORE_DUE: 'Before due',
  DUE_DATE: 'Due date',
  OVERDUE: 'Overdue',
}

/** The standard email a step sends, by its offset from the due date. */
export function reminderEmailStageFor(daysRelativeToDue: number): ReminderEmailStage {
  if (daysRelativeToDue < 0) return 'BEFORE_DUE'
  return daysRelativeToDue === 0 ? 'DUE_DATE' : 'OVERDUE'
}

interface ReminderEmailPreviewSheetProps {
  /** The stage shown; null keeps the sheet closed. */
  stage: ReminderEmailStage | null
  onStageChange: (stage: ReminderEmailStage) => void
  onOpenChange: (isOpen: boolean) => void
}

/**
 * Read-only preview of the three standard reminder emails, rendered from the real
 * template.
 */
export function ReminderEmailPreviewSheet({
  stage,
  onStageChange,
  onOpenChange,
}: ReminderEmailPreviewSheetProps) {
  const isOpen = stage !== null
  const previewQuery = useReminderEmailPreviewQuery(stage ?? 'BEFORE_DUE', isOpen)
  const preview = previewQuery.data?.data ?? null

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[min(100vw,720px)] max-w-none gap-4 overflow-y-auto p-5 sm:max-w-none"
      >
        <SheetHeader className="pr-8">
          <SheetTitle>Reminder email preview</SheetTitle>
          <SheetDescription>
            The standard email your client receives. Sample data is used for the details.
          </SheetDescription>
        </SheetHeader>
        <Tabs
          value={stage ?? 'BEFORE_DUE'}
          onValueChange={(value) => onStageChange(ReminderEmailStageSchema.parse(value))}
        >
          <TabsList>
            {ReminderEmailStageSchema.options.map((option) => (
              <TabsTrigger key={option} value={option}>
                {STAGE_LABELS[option]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {preview ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border">
            <Typography variant="body-sm" className="border-b px-4 py-3 font-semibold">
              {preview.subject}
            </Typography>
            <iframe
              title={`${STAGE_LABELS[preview.stage]} reminder email`}
              srcDoc={preview.html}
              sandbox=""
              className="min-h-[480px] w-full flex-1 bg-white"
            />
          </div>
        ) : previewQuery.isError || previewQuery.data?.error ? (
          <Typography variant="body-sm" color="muted">
            The preview could not be loaded. Try again in a moment.
          </Typography>
        ) : (
          <div role="status" aria-label="Loading email preview" className="space-y-2">
            <Skeleton variant="text" className="w-2/3" />
            <Skeleton className="h-[420px] w-full" />
          </div>
        )}
        <Typography variant="label" color="muted">
          The wording can&apos;t be edited. Your logo, business name and payment options
          are added automatically.
        </Typography>
      </SheetContent>
    </Sheet>
  )
}
